import * as types from '../constants/actionTypes';
import { CALL_API } from '../helpers/api';
import { SUBSCRIPTION_HEADER_NAME } from '../constants/constants';
import { saveFile } from '../helpers/saveFile';
import { API_ROOT } from '../helpers/apiConfig';

// Prevents duplicate API calls
let requestFlags = {};

/**
 * Helper function to dispatch API requests with duplicate prevention
 */
const apiRequest = (key, { endpoint, method = 'GET', body = null, types, signal = null }) => {
	return async (dispatch) => {
		if (requestFlags[key])
			return Promise.resolve({ type: types[1], payload: { message: "Duplicate request prevented" } });

		requestFlags[key] = true;

		try {
			const response = await dispatch({
				[CALL_API]: {
					endpoint,
					method, 
					body,
					types,
					signal
				}
			});

			if (!response || typeof response !== 'object' || !response.type) {
				console.error(`API request error in ${key}: Invalid response`, response);
				return { type: types[2], payload: { message: "Invalid server response" } };
			}

			return response;
		} catch (error) {
			console.error(`API request failed in ${key}:`, error);
			return { type: types[2], payload: { message: error?.message || "Network error occurred" } };
		} finally {
			requestFlags[key] = false;
		}
	};
};

export const actions = {
	loadDashboardFilters: () => apiRequest('loadDashboardFilters', {
		endpoint: 'company-dashboard/filters',
		types: [
			types.LOAD_COMPANY_DASHBOARD_FILTERS_REQUEST,
			types.LOAD_COMPANY_DASHBOARD_FILTERS_SUCCESS,
			types.LOAD_COMPANY_DASHBOARD_FILTERS_FAILURE,
		],
	}),

	loadDashboardFiltersV2: (signal) => apiRequest('loadDashboardFiltersV2', {
		endpoint: 'v2/company-dashboard/filters',
		types: [
			types.LOAD_COMPANY_DASHBOARD_FILTERS_REQUEST,
			types.LOAD_COMPANY_DASHBOARD_FILTERS_SUCCESS,
			types.LOAD_COMPANY_DASHBOARD_FILTERS_FAILURE,
		],
		signal,
	}),

	loadDashboardSettingsV2: (signal) => apiRequest('loadDashboardSettingsV2', {
		endpoint: 'v2/company-dashboard/settings',
		types: [
			types.LOAD_COMPANY_DASHBOARD_SETTINGS_REQUEST,
			types.LOAD_COMPANY_DASHBOARD_SETTINGS_SUCCESS,
			types.LOAD_COMPANY_DASHBOARD_SETTINGS_FAILURE,
		],
		signal,
	}),

	saveSettings: (settings) => apiRequest('saveSettings', {
		endpoint: 'company-dashboard/save-settings',
		method: 'POST',
		body: settings,
		types: [
			types.SAVE_COMPANY_DASHBOARD_SETTINGS_REQUEST,
			types.SAVE_COMPANY_DASHBOARD_SETTINGS_SUCCESS,
			types.SAVE_COMPANY_DASHBOARD_SETTINGS_FAILURE,
		],
	}),

	saveSettingsV2: (settings) => apiRequest('saveSettingsV2', {
		endpoint: 'v2/company-dashboard/settings',
		method: 'POST',
		body: settings,
		types: [
			types.SAVE_COMPANY_DASHBOARD_SETTINGS_REQUEST,
			types.SAVE_COMPANY_DASHBOARD_SETTINGS_SUCCESS,
			types.SAVE_COMPANY_DASHBOARD_SETTINGS_FAILURE,
		],
	}),

	listSettingsV2: () => apiRequest('listSettingsV2', {
		endpoint: 'v2/company-dashboard/list-settings',
		method: 'GET',
		types: [
			types.LIST_COMPANY_DASHBOARD_SETTINGS_REQUEST,
			types.LIST_COMPANY_DASHBOARD_SETTINGS_SUCCESS,
			types.LIST_COMPANY_DASHBOARD_SETTINGS_FAILURE,
		]
	}),
	
	saveCompanyDashboardCustomViews: (request) => apiRequest('saveSettingsV2', {
		endpoint: 'v2/company-dashboard/save-company-dashboard-custom-views',
		method: 'POST',
		body: request,
		types: [
			types.SAVE_CUSTOM_VIEWS_COMPANY_DASHBOARD_SETTINGS_REQUEST,
			types.SAVE_CUSTOM_VIEWS_COMPANY_DASHBOARD_SETTINGS_SUCCESS,
			types.SAVE_CUSTOM_VIEWS_COMPANY_DASHBOARD_SETTINGS_FAILURE,
		],
	}),

	updateSettingsV2: (settings, id) => apiRequest(`updateSettingsV2-${id}`, {
		endpoint: `v2/company-dashboard/settings/${id}`,
		method: 'PUT',
		body: settings,
		types: [
			types.UPDATE_COMPANY_DASHBOARD_SETTINGS_REQUEST,
			types.UPDATE_COMPANY_DASHBOARD_SETTINGS_SUCCESS,
			types.UPDATE_COMPANY_DASHBOARD_SETTINGS_FAILURE,
		],
	}),

	checkSettingsAvailabilityV2: (settings, signal) => apiRequest('checkSettingsAvailabilityV2', {
		endpoint: 'v2/company-dashboard/availability',
		method: 'POST',
		body: settings,
		types: [
			types.CHECK_COMPANY_DASHBOARD_SETTINGS_REQUEST,
			types.CHECK_COMPANY_DASHBOARD_SETTINGS_SUCCESS,
			types.CHECK_COMPANY_DASHBOARD_SETTINGS_FAILURE,
		],
		signal,
	}),

	loadLocationSummary: (filters, pageSize, pageNumber) => apiRequest('loadLocationSummary', {
		endpoint: 'company-dashboard/location-summary',
		method: 'POST',
		body: { ...filters, pageSize, pageNumber },
		types: [
			types.LOAD_COMPANY_DASHBOARD_LOCSUMMARY_REQUEST,
			types.LOAD_COMPANY_DASHBOARD_LOCSUMMARY_SUCCESS,
			types.LOAD_COMPANY_DASHBOARD_LOCSUMMARY_FAILURE,
		],
	}),

	loadProjects: (filters, pageSize, pageNumber, signal) => apiRequest('loadProjects', {
			endpoint: 'company-dashboard/projects',
			method: 'POST',
			body: { ...filters, pageSize, pageNumber },
			types: [
				types.LOAD_COMPANY_DASHBOARD_PROJECTS_REQUEST,
				types.LOAD_COMPANY_DASHBOARD_PROJECTS_SUCCESS,
				types.LOAD_COMPANY_DASHBOARD_PROJECTS_FAILURE,
			],
			signal,
		}),

	loadLocations: (projectId, sliceName, barTitle, filters) => apiRequest(`loadLocations-${projectId}`, {
		endpoint: 'company-dashboard/locations',
		method: 'POST',
		body: { projectId, sliceName, barTitle, format: 'json', ...filters },
		types: [
			types.LOAD_COMPANY_DASHBOARD_LOCATIONS_REQUEST,
			types.LOAD_COMPANY_DASHBOARD_LOCATIONS_SUCCESS,
			types.LOAD_COMPANY_DASHBOARD_LOCATIONS_FAILURE,
		],
	}),

	exportLocations: (projectId, sliceName, barTitle, filters) => {
		return async (dispatch, getState) => {
			if (requestFlags.exportLocations) return;
			requestFlags.exportLocations = true;

			const endpoint = 'company-dashboard/locations';
			const method = 'POST';
			let headers = { 'Content-Type': 'application/json' };
			const token = getState().user.accessToken;
			if (typeof token === 'string') {
				headers.Authorization = `Bearer ${token}`;
			}
			const subscriptionId = getState().user.currentSubscriptionId;
			if (subscriptionId) {
				headers[SUBSCRIPTION_HEADER_NAME] = subscriptionId;
			}

			const body = { projectId, sliceName, barTitle, format: 'excel', ...filters };

			try {
				const response = await fetch(`${API_ROOT}${endpoint}`, {
					method,
					headers,
					body: JSON.stringify(body),
					credentials: 'same-origin',
				});
				await saveFile(response);
			} finally {
				requestFlags.exportLocations = false;
			}
		};
	},

	loadMissedMilestones: (searchParams) => apiRequest('loadMissedMilestones', {
		endpoint: 'company-dashboard/missed-milestones',
		method: 'POST',
		body: searchParams,
		types: [
			types.LOAD_MISSED_MILESTONES_REQUEST,
			types.LOAD_MISSED_MILESTONES_SUCCESS,
			types.LOAD_MISSED_MILESTONES_FAILURE,
		],
	}),

	loadUpcomingTasks: (searchParams) => apiRequest('loadUpcomingTasks', {
		endpoint: 'company-dashboard/upcoming-Tasks',
		method: 'POST',
		body: searchParams,
		types: [
			types.LOAD_UPCOMING_TASKS_REQUEST,
			types.LOAD_UPCOMING_TASKS_SUCCESS,
			types.LOAD_UPCOMING_TASKS_FAILURE,
		],
	}),

	loadWorkload: (filters, view, divider, secondaryDivider, pageNumber) => apiRequest('loadWorkload', {
		endpoint: 'company-dashboard/workload',
		method: 'POST',
		body: { ...filters, view, divider, secondaryDivider, pageNumber },
		types: [
			types.LOAD_WORKLOAD_RESULTS_REQUEST,
			types.LOAD_WORKLOAD_RESULTS_SUCCESS,
			types.LOAD_WORKLOAD_RESULTS_FAILURE,
		],
	}),

	getWorkloadDetails: (filters, divider, seriesName) => apiRequest('getWorkloadDetails', {
		endpoint: 'company-dashboard/workload-details',
		method: 'POST',
		body: { ...filters, divider, seriesName },
		types: [
			types.GET_WORKLOAD_DETAILS_REQUEST,
			types.GET_WORKLOAD_DETAILS_SUCCESS,
			types.GET_WORKLOAD_DETAILS_FAILURE,
		],
	}),

	getProjectParams: (request) => apiRequest('getProjectParams', {
		endpoint: 'project/get-params',
		method: 'POST',
		body: request,
		types: [
			types.GET_PROJECT_PARAMS_REQUEST,
			types.GET_PROJECT_PARAMS_SUCCESS,
			types.GET_PROJECT_PARAMS_FAILURE
		]
	}),
};
